import { Component, OnInit, AfterViewInit, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VendorService } from 'src/app/services/vendor/vendor.service';
import { VendorModalService } from 'src/app/services/vendor-modal/vendor-modal.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-md-vendors-listing',
  templateUrl: './md-vendors-listing.component.html',
  styleUrls: ['./md-vendors-listing.component.scss']
})
export class MdVendorsListingComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() vendorAdded = new EventEmitter<void>();
  @Output() vendorUpdated = new EventEmitter<void>();

  // Math reference for template
  Math = Math;

  // Helper method to display vendor types
  getVendorTypesDisplay(vendorTypes: any): string {
    if (!vendorTypes) return '-';

    if (Array.isArray(vendorTypes)) {
      return vendorTypes.join(', ');
    }

    return vendorTypes.toString();
  }

  // Data properties
  vendors: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    search: '',
    date_from: '',
    date_to: ''
  };

  constructor(
    private _vendorService: VendorService,
    private _vendorModalService: VendorModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadVendors();
    this.setupModalSubscriptions();
  }

  ngAfterViewInit(): void {
    this.initializeMenus();
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  private setupModalSubscriptions(): void {
    // Listen for vendor updates
    this.subscriptions.push(
      this._vendorModalService.selectedVendor$.subscribe(vendor => {
        if (vendor) {
          // Vendor selected for editing
        }
      })
    );
  }

  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  loadVendors(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    delete filters.search;
    this.subscriptions.push(
      this._vendorService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.vendors = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.vendors = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading vendors:', error);
          this.vendors = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadVendors(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadVendors(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: ''
    };
    this.searchTerm = '';
    this.loadVendors(1, '', {});
  }

  openAddVendorModal(): void {
    this._vendorModalService.clearSelectedVendor();
    const modalElement = document.getElementById('kt_modal_vendor_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditVendorModal(vendor: any): void {
    this._vendorModalService.setSelectedVendor(vendor);
    const modalElement = document.getElementById('kt_modal_vendor_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteVendor(vendor: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete vendor "${vendor.company_name || 'this vendor'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(vendor);
      }
    });
  }

  private performDelete(vendor: any): void {
    this.subscriptions.push(
      this._vendorService.DeleteDataById(vendor._id).subscribe({
        next: (response: any) => {
          console.log('Vendor deleted successfully:', response);

          Swal.fire({
            text: "Vendor has been successfully deleted!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });

          // Refresh the list
          this.loadVendors(this.currentPage, this.searchTerm, this.filters);
        },
        error: (error) => {
          console.error('Error deleting vendor:', error);

          Swal.fire({
            text: error.error?.message || "Sorry, looks like there are some errors detected, please try again.",
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn btn-primary"
            }
          });
        }
      })
    );
  }

  onVendorAdded(): void {
    console.log('Vendor added, refreshing list...');
    this.loadVendors(this.currentPage, this.searchTerm, this.filters);
    this.vendorAdded.emit();
  }

  onVendorUpdated(): void {
    console.log('Vendor updated, refreshing list...');
    this.loadVendors(this.currentPage, this.searchTerm, this.filters);
    this.vendorUpdated.emit();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }
}
